import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createPortal } from 'react-dom';

import { ThemeProvider } from 'styled-components';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { Alert } from '@interstate/components/Alert';
import { Typography } from '@interstate/components/Typography';
import { Spinner } from '@interstate/components/Spinner';
import { BuyerEmailChangeNotice, CreditConsentBanner, DiscardChangesModal } from '@makemydeal/dr-shared-components';
import type { InsuranceFormRef } from '@makemydeal/dr-shared-components';

import CustomerForm from './CustomerForm';
import CustomerCardTabs from './CustomerCardTabs';
import InsuranceTab from './InsuranceTab';
import CustomerCardErrorBoundary from './CustomerCardErrorBoundary';
import { CustomerCardErrorAlert } from './CustomerCardErrorAlert';
import { CardWrapper, LoadingOverlay } from './CustomerCard.styles';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { useCustomerCardActions } from '../hooks/useCustomerCardActions';
import { useCustomerCardSelector } from '../hooks/useCustomerCardSelector';
import * as customerCardSelectors from '../store/selectors';
import * as customerSpdSelectors from '../store/customerSpdSelectors';
import * as businessConversionSelectors from '../store/businessConversionSelectors';
import { BUSINESS, CARD_OPERATION_DISCARD_MESSAGE, CARD_OPERATION_KIND, CARD_OPERATION_TARGET } from '../constants';
import { CUSTOMER_CARD_NR_EVENTS } from '../analytics';
import { scrollElementIntoView } from '../utils/scroll.utils';
import { logCustomerCardError, logCustomerCardPageAction } from '../utils/newRelicLogging';
import { startOperation, continueOperation, goBack, cancelCardOperation } from '../store/cardOperationActionCreators';
import SearchView from './search/SearchView';
import { getSearchDescriptor } from './search/searchDescriptors';
import type { SearchResult } from '../types/search.types';
import {
    CUSTOMER_TABS,
    type CardOperationKind,
    type CustomerCardCapabilities,
    type CustomerCardHostState,
    type CustomerCardImperativeRef,
    type CustomerCardSaveData,
    type CustomerFormRef,
    type CustomerSubTabKey,
    type CustomerTabKey,
    type OnAnalyticsEvent
} from '../types';
import { CUSTOMER_CARD_ANALYTICS } from '../analytics';

type UnifiedCustomerCardProps = {
    dealId?: string;
    onSave?: (data: CustomerCardSaveData) => void;
    onCancel?: () => void;
    onStateChange?: (state: CustomerCardHostState) => void;
    capabilities?: CustomerCardCapabilities;
    /** When true, suppresses the built-in saving overlay; the host receives isSaving via onStateChange to handle loading itself. */
    hideLoader?: boolean;
    hideCheckLoader?: boolean;
    /**
     * When provided, overrides the store's initialTab to open the card on a specific tab.
     * Pass `undefined` when the slideout is closed so the tab is not reset mid-session.
     */
    initialTab?: CustomerTabKey | typeof CUSTOMER_TABS.INSURANCE;
    /** Identifies the host app for NR instrumentation (e.g. 'MV', 'SCI'). */
    hostApp?: 'MV' | 'SCI';
    /**
     * Host handler for analytics events emitted by the card (e.g. Show SPD). The activity emits a
     * neutral event name (see CUSTOMER_CARD_ANALYTICS); the host maps it to its own analytics stack.
     * Optional — hosts that don't track analytics simply omit it.
     */
    onAnalyticsEvent?: OnAnalyticsEvent;
    /** Called after a successful business conversion API call with the new IDs. */
    onBusinessConversionComplete?: (data: { commonConsumerId: string; businessEntityId: string }) => void;
    /**
     * Optional opt-in render-slot rendered beside the buyer form's "Personal Information" heading
     * (e.g. SCI's "Convert to Business" entry CTA). Omitted by hosts that don't use it (e.g. MV) —
     * additive only, does not affect any existing consumer.
     */
    personalInfoHeaderAction?: ReactNode;
    /**
     * Opt-in: render the auto-dismissing "Customer details saved." success banner. Only the SCI
     * page-embedded card enables this; the slideout hosts (SCI and MV) leave it off and simply
     * close on save, so success surfaces solely on the persistent page instance (which reads the
     * same shared saveStatus, so it also lights up when the save happened in the slideout).
     */
    showSaveSuccessBanner?: boolean;
};

export type UnifiedCustomerCardRef = CustomerCardImperativeRef;

const SAVE_SUCCESS_MESSAGE = 'Customer details saved.';
const SAVE_SUCCESS_BANNER_AUTO_DISMISS_MS = 5000;

// Maps the three content panes to a single discriminant key used for portal routing.
type ContentTabKey = CustomerTabKey | typeof CUSTOMER_TABS.INSURANCE;

// Reason the prompt shows and what confirming does.
type DiscardContext = { reason: 'cancel' } | { reason: 'back' } | { reason: 'operation'; kind: CardOperationKind };

const useDetachedNode = (): HTMLDivElement => {
    const ref = useRef<HTMLDivElement | null>(null);
    if (ref.current === null) {
        ref.current = document.createElement('div');
    }
    return ref.current;
};

const detailsSubTabFor = (customer: CustomerTabKey): CustomerSubTabKey =>
    customer === CUSTOMER_TABS.CO_BUYER ? CUSTOMER_TABS.CO_BUYER_DETAILS : CUSTOMER_TABS.BUYER_DETAILS;

// Insurance is a sub-tab, so a requested `insurance` opens the Buyer container with the Insurance sub-tab active.
const resolveInitialTabs = (requested: ContentTabKey, hasCoBuyer: boolean): { tab: CustomerTabKey; subTab: CustomerSubTabKey } => {
    if (requested === CUSTOMER_TABS.INSURANCE) return { tab: CUSTOMER_TABS.BUYER, subTab: CUSTOMER_TABS.INSURANCE };
    if (requested === CUSTOMER_TABS.CO_BUYER && hasCoBuyer)
        return { tab: CUSTOMER_TABS.CO_BUYER, subTab: CUSTOMER_TABS.CO_BUYER_DETAILS };
    return { tab: CUSTOMER_TABS.BUYER, subTab: CUSTOMER_TABS.BUYER_DETAILS };
};

const UnifiedCustomerCard = forwardRef<UnifiedCustomerCardRef, UnifiedCustomerCardProps>(
    (
        {
            dealId,
            onSave,
            onCancel,
            onStateChange,
            capabilities,
            hideLoader,
            hideCheckLoader,
            initialTab,
            hostApp,
            onAnalyticsEvent,
            onBusinessConversionComplete,
            personalInfoHeaderAction,
            showSaveSuccessBanner = false
        },
        ref
    ) => {
        const theme = useInterstateTheme();
        const dispatch = useDispatch();

        const buyerFormRef = useRef<CustomerFormRef>(null);
        const coBuyerFormRef = useRef<CustomerFormRef>(null);
        const insuranceFormRef = useRef<InsuranceFormRef>(null);

        /**
         * UnifiedCustomerCard intentionally reads from the activity-local store.
         * MV and SCI will share the same apply orchestration, loading, validation,
         * and inline error handling, so keeping the unified card store-backed gives
         * that workflow a single source of truth instead of duplicating host-specific
         * data mapping in each app. If a future consumer only needs read-only rendering
         * without the activity slice, optional props can be added back as an override
         * layer, but we are not carrying that API now because no current consumer needs
         * it and two active inputs would make saved/error state drift easier.
         */
        const buyer = useCustomerCardSelector(customerCardSelectors.getBuyer);
        const coBuyer = useCustomerCardSelector(customerCardSelectors.getCoBuyer);
        const hasCoBuyer = useCustomerCardSelector(customerCardSelectors.getHasCoBuyer);
        const dealerId = useCustomerCardSelector(customerCardSelectors.getDealerId);
        const storeInitialTab = useCustomerCardSelector(customerCardSelectors.getInitialTab) ?? CUSTOMER_TABS.BUYER;
        // initialTab prop (from host) takes precedence over the store value so the host can
        // navigate directly to the co-buyer tab when the slideout opens via the co-buyer CTA.
        const initialCustomer = initialTab ?? storeInitialTab;
        const saveStatus = useCustomerCardSelector(customerCardSelectors.getSaveStatus);
        const isSpdCheckLoading = useCustomerCardSelector(customerSpdSelectors.getSpdCheckLoading);
        const saveError = useCustomerCardSelector(customerCardSelectors.getSaveError);
        const vehicleInsuranceInfo = useCustomerCardSelector(customerCardSelectors.getVehicleInsuranceInfo);
        const creditConsentJustGranted = useCustomerCardSelector(customerCardSelectors.getCreditConsentJustGranted);
        const isVehicleInsuranceUpdated = useCustomerCardSelector(customerCardSelectors.getIsVehicleInsuranceUpdated);

        const coBuyerDraft = useCustomerCardSelector(customerCardSelectors.getCoBuyerDraft);
        const activeFormRefs = useMemo(
            () => ({
                buyerRef: buyerFormRef,
                coBuyerRef: hasCoBuyer || Boolean(coBuyerDraft) ? coBuyerFormRef : undefined,
                insuranceRef: insuranceFormRef
            }),
            [hasCoBuyer, coBuyerDraft]
        );

        const [isBuyerEmailChangeNoticeOpen, setIsBuyerEmailChangeNoticeOpen] = useState(false);
        const resolveBuyerEmailChangeNoticeRef = useRef<((confirmed: boolean) => void) | null>(null);

        const confirmBuyerEmailChange = useCallback(
            () =>
                new Promise<boolean>((resolve) => {
                    resolveBuyerEmailChangeNoticeRef.current = resolve;
                    setIsBuyerEmailChangeNoticeOpen(true);
                }),
            []
        );

        const closeBuyerEmailChangeNotice = useCallback((confirmed: boolean) => {
            setIsBuyerEmailChangeNoticeOpen(false);
            resolveBuyerEmailChangeNoticeRef.current?.(confirmed);
            resolveBuyerEmailChangeNoticeRef.current = null;
        }, []);

        const { apply, cancel } = useCustomerCardActions({
            formRefs: activeFormRefs,
            dealId,
            onSave,
            onBusinessConversionComplete,
            confirmBuyerEmailChange: capabilities?.warnOnBuyerEmailChange ? confirmBuyerEmailChange : undefined
        });

        const operation = useCustomerCardSelector(customerCardSelectors.getCardOperation);
        const canContinue = useCustomerCardSelector(customerCardSelectors.canContinueOperation);
        const selectedSearchResult = useCustomerCardSelector(customerCardSelectors.getSelectedSearchResult);
        const isBusinessConversionInProgress = useCustomerCardSelector(businessConversionSelectors.getIsBusinessEditStep);
        const isSearchStep = useCustomerCardSelector(customerCardSelectors.getIsSearchStep);
        const operationOwnsCard = useCustomerCardSelector(customerCardSelectors.getOperationOwnsCard);
        const canGoBack = useCustomerCardSelector(customerCardSelectors.getCanGoBack);
        const isBuyerMissing = useCustomerCardSelector(customerCardSelectors.getIsBuyerMissing);
        const buyerDraft = useCustomerCardSelector(customerCardSelectors.getBuyerDraft);
        const isAnyOperationInProgress = Boolean(buyerDraft) || Boolean(coBuyerDraft);
        // Whichever participant the running operation targets is the one a draft replaces.
        const targetParticipant = useCustomerCardSelector(customerCardSelectors.getOperationTargetParticipant);
        const isBusinessBuyer = buyer?.customerType === BUSINESS || isBusinessConversionInProgress;

        const [buyerDirty, setBuyerDirty] = useState(false);
        const [coBuyerDirty, setCoBuyerDirty] = useState(false);
        const [insuranceDirty, setInsuranceDirty] = useState(false);
        const [buyerValid, setBuyerValid] = useState(true);
        const [coBuyerValid, setCoBuyerValid] = useState(true);
        const [insuranceValid, setInsuranceValid] = useState(true);

        // A drafted record can be missing fields the deal requires, so the gaps are announced up front —
        // the dealer can neither save nor select it until they're filled.
        const isDraftedRecordIncomplete = Boolean((buyerDraft && !buyerValid) || (coBuyerDraft && !coBuyerValid));
        const [buyerHasContractErrors, setBuyerHasContractErrors] = useState(false);
        const [coBuyerHasContractErrors, setCoBuyerHasContractErrors] = useState(false);
        const [insuranceHasContractErrors, setInsuranceHasContractErrors] = useState(false);
        const [discardContext, setDiscardContext] = useState<DiscardContext | null>(null);
        const [hasRenderError, setHasRenderError] = useState(false);
        const [isSaveSuccessVisible, setIsSaveSuccessVisible] = useState(false);
        const [consentSentAt, setConsentSentAt] = useState<string | undefined>(undefined);

        const isGCDynamicValidationsEnabled = useSelector(featureToggleSelectors.isGCDynamicValidationsEnabled);
        const showContractingAlert =
            Boolean(capabilities?.contractValidation) &&
            isGCDynamicValidationsEnabled &&
            (buyerHasContractErrors || coBuyerHasContractErrors || insuranceHasContractErrors);
        const creditConsentToggle = useSelector(featureToggleSelectors.isCreditAppConsentEnabled);
        const isCreditConsentEnabled = capabilities?.enableCreditConsent ?? creditConsentToggle;
        const isPostalCodeDigitsOnlyValidationEnabled = useSelector(featureToggleSelectors.isPostalCodeDigitsOnlyValidationEnabled);

        useEffect(() => {
            if (!showSaveSuccessBanner || saveStatus !== 'success') {
                setIsSaveSuccessVisible(false);
                return;
            }
            setIsSaveSuccessVisible(true);
            const timer = setTimeout(() => setIsSaveSuccessVisible(false), SAVE_SUCCESS_BANNER_AUTO_DISMISS_MS);
            return () => clearTimeout(timer);
        }, [saveStatus, showSaveSuccessBanner]);

        // Shown once, for the save that actually granted consent — the copy announces a one-time event
        // ("SSN sent"), so re-showing it on later saves would be misleading. The reducer detects the
        // transition; reopening the card re-seeds the flag, which is what clears the banner. SCI hides the
        // "SSN sent" line behind DE770962_Process_SCI_DT_Updates, leaving only the title.
        const isConsentBannerVisible = isCreditConsentEnabled && creditConsentJustGranted;
        const showConsentSentMessage = !capabilities?.enableCreditConsentSentMessageSuppression;

        // No persisted send time exists — DealXG stores only the boolean. That is workable because the
        // banner is one-shot: it renders only on the save that granted consent.
        useEffect(() => {
            if (isConsentBannerVisible) {
                setConsentSentAt(new Date().toISOString());
            }
        }, [isConsentBannerVisible]);

        // Every banner renders at the top of the card, but the consent checkbox sits at the bottom of
        // the form — so a user saving from there would never see the outcome.
        const cardRef = useRef<HTMLDivElement>(null);
        useEffect(() => {
            if (saveStatus === 'success' || saveStatus === 'error') {
                scrollElementIntoView(cardRef.current);
            }
        }, [saveStatus]);

        // Track the live email of each form so the duplicate-email validation rule
        // always compares against the other customer's current (unsaved) value,
        // not the stale value in the Redux store.
        const [buyerLiveEmail, setBuyerLiveEmail] = useState<string | undefined>(buyer?.email);
        const [coBuyerLiveEmail, setCoBuyerLiveEmail] = useState<string | undefined>(coBuyer?.email);

        const handleBuyerFormChange = useCallback(
            (dirty: boolean, isValid: boolean, email?: string, hasContractErrors?: boolean) => {
                setBuyerDirty(dirty);
                setBuyerValid(isValid);
                setBuyerLiveEmail(email);
                setBuyerHasContractErrors(Boolean(hasContractErrors));
            },
            []
        );
        const handleCoBuyerFormChange = useCallback(
            (dirty: boolean, isValid: boolean, email?: string, hasContractErrors?: boolean) => {
                setCoBuyerDirty(dirty);
                setCoBuyerValid(isValid);
                setCoBuyerLiveEmail(email);
                setCoBuyerHasContractErrors(Boolean(hasContractErrors));
            },
            []
        );
        const handleInsuranceFormChange = useCallback((dirty: boolean, isValid: boolean) => {
            setInsuranceDirty(dirty);
            setInsuranceValid(isValid);
        }, []);

        const handleInsuranceContractErrors = useCallback((hasErrors: boolean) => {
            setInsuranceHasContractErrors(hasErrors);
        }, []);

        // The store's selection outlives the choice so Back can return to it, so record the decision here.
        const [isDraftFromSelection, setIsDraftFromSelection] = useState(false);

        const advanceToEdit = useCallback(
            (selected: SearchResult | null, typedText: string) => {
                const descriptor = getSearchDescriptor(operation?.kind);
                if (!descriptor) return;
                setIsDraftFromSelection(Boolean(selected));
                dispatch(
                    continueOperation(
                        descriptor.buildDraft({
                            selected,
                            typedText,
                            participant: targetParticipant,
                            isPostalCodeDigitsOnlyValidationEnabled
                        })
                    )
                );
            },
            [dispatch, operation?.kind, targetParticipant, isPostalCodeDigitsOnlyValidationEnabled]
        );

        const handleAddNew = useCallback((typedText: string) => advanceToEdit(null, typedText), [advanceToEdit]);

        const handleContinueOperation = useCallback(
            () => advanceToEdit(selectedSearchResult, ''),
            [advanceToEdit, selectedSearchResult]
        );

        const handleCancelOperation = useCallback(() => {
            dispatch(cancelCardOperation());
        }, [dispatch]);

        // The forms unmount at the search step, so their dirty flags are stale there — nothing to discard.
        const hasFormEdits = !isSearchStep && (buyerDirty || coBuyerDirty || insuranceDirty);

        // Picking a different person is itself the change, so Apply enables without a field edit.
        const hasUnsavedWork = hasFormEdits || Boolean(buyerDraft || coBuyerDraft);

        const hasValidationErrors = !buyerValid || !coBuyerValid || !insuranceValid;

        // Starts operations that replace forms; warns before discarding unsaved edits.
        const requestOperation = useCallback(
            (kind: CardOperationKind) => {
                if (hasFormEdits && kind in CARD_OPERATION_DISCARD_MESSAGE) {
                    setDiscardContext({ reason: 'operation', kind });
                } else {
                    dispatch(startOperation(kind));
                }
            },
            [dispatch, hasFormEdits]
        );

        const exitOwnedCard = useCallback(() => {
            cancel();
            onCancel?.();
        }, [cancel, onCancel]);

        const handleOperationBack = useCallback(() => {
            if (hasFormEdits) {
                setDiscardContext({ reason: 'back' });
            } else if (operationOwnsCard) {
                exitOwnedCard();
            } else {
                dispatch(goBack());
            }
        }, [dispatch, hasFormEdits, operationOwnsCard, exitOwnedCard]);

        const hasMissingBuyerError = Boolean(capabilities?.requireBuyer) && isBuyerMissing && !operation;

        const initial = resolveInitialTabs(initialCustomer, hasCoBuyer);
        const [activeTab, setActiveTab] = useState<CustomerTabKey>(initial.tab);
        const [activeSubTab, setActiveSubTab] = useState<CustomerSubTabKey>(initial.subTab);

        useEffect(() => {
            if (!hasMissingBuyerError) return;
            logCustomerCardError(
                CUSTOMER_CARD_NR_EVENTS.RENDERED_WITHOUT_BUYER,
                'Customer card rendered with no buyer and no operation running'
            );
        }, [hasMissingBuyerError]);

        useEffect(() => {
            // An operation edits one participant and hides the other's tab, so Apply follows the target
            // alone — the other's errors are unreachable from here, and contract rules make them likely.
            const target = operation ? CARD_OPERATION_TARGET[operation.kind] : null;
            const isTargetValid = target === CUSTOMER_TABS.CO_BUYER ? coBuyerValid : buyerValid;
            const isValid = target ? isTargetValid : buyerValid && coBuyerValid && insuranceValid;
            onStateChange?.({
                canApply: hasUnsavedWork && isValid,
                canCancel: true,
                isSaving: saveStatus === 'saving',
                hasRenderError: hasRenderError || hasMissingBuyerError,
                canContinue,
                operation,
                operationTitle: getSearchDescriptor(operation?.kind)?.heading,
                canGoBack,
                activeSubTab
            });
        }, [
            hasUnsavedWork,
            buyerValid,
            coBuyerValid,
            insuranceValid,
            saveStatus,
            onStateChange,
            hasRenderError,
            hasMissingBuyerError,
            canContinue,
            operation,
            canGoBack,
            activeSubTab
        ]);

        const handleCancelWithDiscardCheck = useCallback(() => {
            // An operation that owns the card displaces nobody, so an untouched search panel just closes.
            const shouldPrompt = operationOwnsCard ? hasUnsavedWork : hasFormEdits || Boolean(operation);
            if (shouldPrompt) {
                setDiscardContext({ reason: 'cancel' });
            } else {
                cancel();
                onCancel?.();
            }
        }, [cancel, hasFormEdits, hasUnsavedWork, operation, onCancel, operationOwnsCard]);

        const pendingOperation = discardContext?.reason === 'operation' ? discardContext.kind : null;
        // An operation prompt reports the operation it blocked; the others report what they were doing.
        const discardActionLabel = pendingOperation ?? discardContext?.reason;

        const handleDiscardConfirm = useCallback(() => {
            setDiscardContext(null);
            logCustomerCardPageAction('CustomerCardOperation', {
                event: 'discardModal',
                outcome: 'discard',
                discardAction: discardActionLabel,
                operationId: operation?.operationId,
                dealXgId: dealId,
                dealerId
            });
            // Checked before the operation-scoped cases: this prompt precedes the operation it starts.
            if (pendingOperation) {
                dispatch(startOperation(pendingOperation));
            } else if (operationOwnsCard) {
                exitOwnedCard();
            } else if (discardActionLabel === 'back') {
                dispatch(goBack());
            } else if (operation) {
                handleCancelOperation();
            } else {
                cancel();
                onCancel?.();
            }
        }, [
            cancel,
            dispatch,
            handleCancelOperation,
            operation,
            onCancel,
            pendingOperation,
            discardActionLabel,
            operationOwnsCard,
            exitOwnedCard,
            dealId,
            dealerId
        ]);

        const handleDiscardBack = useCallback(() => {
            logCustomerCardPageAction('CustomerCardOperation', {
                event: 'discardModal',
                outcome: 'wentBack',
                discardAction: discardActionLabel,
                operationId: operation?.operationId,
                dealXgId: dealId,
                dealerId
            });
            setDiscardContext(null);
        }, [discardActionLabel, operation, dealId, dealerId]);

        useEffect(() => {
            const next = resolveInitialTabs(initialCustomer, hasCoBuyer);
            setActiveTab(next.tab);
            setActiveSubTab(next.subTab);
        }, [hasCoBuyer, initialCustomer]);

        const handleTabChange = useCallback((next: CustomerTabKey) => {
            setActiveTab(next);
            setActiveSubTab(detailsSubTabFor(next));
        }, []);

        // Only the tab control routes through here, so the event stays a user click — the
        // programmatic setActiveSubTab calls above deliberately don't emit it.
        const handleSubTabChange = useCallback(
            (next: CustomerSubTabKey) => {
                setActiveSubTab(next);
                onAnalyticsEvent?.(CUSTOMER_CARD_ANALYTICS.CLICKED_CUSTOMER_SUB_TAB, { subTab: next });
            },
            [onAnalyticsEvent]
        );

        // An operation acts on one participant, so it opens on that participant's details and stays there
        // once it ends. The ref skips mount so a host-requested initial tab survives.
        const lastOperationRef = useRef(operation);
        useEffect(() => {
            if (lastOperationRef.current === operation) return;
            lastOperationRef.current = operation;
            const target = operation ? CARD_OPERATION_TARGET[operation.kind] : activeTab;
            setActiveTab(target);
            setActiveSubTab(detailsSubTabFor(target));
            if (!operation) setIsDraftFromSelection(false);
        }, [operation, activeTab]);

        // Detached DOM nodes — each content pane lives here and is never unmounted
        // when tabs are switched. Only the active node is attached to the visible mount.
        const buyerHost = useDetachedNode();
        const coBuyerHost = useDetachedNode();
        const insuranceHost = useDetachedNode();

        const hostByTab: Record<ContentTabKey, HTMLDivElement> = {
            [CUSTOMER_TABS.BUYER]: buyerHost,
            [CUSTOMER_TABS.CO_BUYER]: coBuyerHost,
            [CUSTOMER_TABS.INSURANCE]: insuranceHost
        };

        const activeTabKey = useMemo((): ContentTabKey => {
            if (activeSubTab === CUSTOMER_TABS.INSURANCE) return CUSTOMER_TABS.INSURANCE;
            return activeTab === CUSTOMER_TABS.CO_BUYER ? CUSTOMER_TABS.CO_BUYER : CUSTOMER_TABS.BUYER;
        }, [activeTab, activeSubTab]);

        const activeHost = hostByTab[activeTabKey];

        const visibleMountRef = useCallback(
            (node: HTMLDivElement | null) => {
                if (node) {
                    node.replaceChildren(activeHost);
                    // On entering the conversion form, focus the Business Name field (parity with MV host).
                    if (isBusinessConversionInProgress) {
                        const businessNameInput = activeHost.querySelector(
                            'input[name="business-name"]'
                        ) as HTMLInputElement | null;
                        businessNameInput?.focus?.();
                    }
                }
            },
            [activeHost, isBusinessConversionInProgress]
        );

        useImperativeHandle(
            ref,
            () => ({
                apply,
                cancel: handleCancelWithDiscardCheck,
                convertToBusiness: () => requestOperation(CARD_OPERATION_KIND.CONVERT_TO_BUSINESS),
                continueOperation: handleContinueOperation,
                cancelOperation: handleCancelOperation,
                goBack: handleOperationBack
            }),
            [
                apply,
                handleCancelWithDiscardCheck,
                requestOperation,
                handleContinueOperation,
                handleCancelOperation,
                handleOperationBack
            ]
        );

        if (hasMissingBuyerError) {
            return (
                <ThemeProvider theme={theme}>
                    <CardWrapper ref={cardRef} data-testid="unified-customer-card">
                        <CustomerCardErrorAlert testId="customer-card-missing-buyer" />
                    </CardWrapper>
                </ThemeProvider>
            );
        }

        return (
            <ThemeProvider theme={theme}>
                <CardWrapper ref={cardRef} data-testid="unified-customer-card">
                    {((saveStatus === 'saving' && !hideLoader) || (isSpdCheckLoading && !hideCheckLoader)) && (
                        <LoadingOverlay data-testid="customer-card-loading">
                            <Spinner size={60} />
                        </LoadingOverlay>
                    )}
                    <CustomerCardErrorBoundary onError={() => setHasRenderError(true)}>
                        {/* These report on the saved participant, so they are not shown during an operation. */}
                        {!operation && (
                            <>
                                {saveError && (
                                    <Alert type="error" sx={{ marginBottom: '1rem' }} data-testid="customer-card-save-error">
                                        <Typography variant="body-sm" tag="span">
                                            {saveError}
                                        </Typography>
                                    </Alert>
                                )}
                                {/* The consent banner is itself a green success alert, so showing both would
                                    stack two near-identical messages. Consent supersedes the generic one. */}
                                {isSaveSuccessVisible && !isConsentBannerVisible && (
                                    <Alert type="success" sx={{ marginBottom: '1rem' }} data-testid="customer-card-save-success">
                                        <Typography variant="body-sm" tag="span">
                                            {SAVE_SUCCESS_MESSAGE}
                                        </Typography>
                                    </Alert>
                                )}
                                {isConsentBannerVisible && (
                                    <CreditConsentBanner sentAt={consentSentAt} showMessage={showConsentSentMessage} />
                                )}
                                {showContractingAlert && (
                                    <Alert
                                        type="error"
                                        sx={{ marginBottom: '1rem' }}
                                        data-testid="activities-contracting-alert-banner"
                                    >
                                        <Typography variant="body-sm" tag="span">
                                            Cannot generate contract. Please fix the highlighted details before continuing.
                                        </Typography>
                                    </Alert>
                                )}
                            </>
                        )}

                        <DiscardChangesModal
                            show={discardContext !== null}
                            onDiscard={handleDiscardConfirm}
                            onGoBack={handleDiscardBack}
                            hasValidationErrors={hasValidationErrors}
                            message={
                                pendingOperation && !hasValidationErrors
                                    ? CARD_OPERATION_DISCARD_MESSAGE[pendingOperation]
                                    : undefined
                            }
                        />

                        <BuyerEmailChangeNotice
                            show={isBuyerEmailChangeNoticeOpen}
                            onConfirm={() => closeBuyerEmailChangeNotice(true)}
                            onClose={() => closeBuyerEmailChangeNotice(false)}
                        />

                        {isSearchStep ? (
                            <SearchView onAddNew={handleAddNew} />
                        ) : (
                            <>
                                {isDraftedRecordIncomplete && (
                                    <Alert
                                        type="error"
                                        sx={{ marginBottom: '1rem' }}
                                        data-testid="customer-card-incomplete-record-alert"
                                    >
                                        <Typography variant="strong-sm" tag="span" sx={{ display: 'block' }}>
                                            Customer record is missing required information
                                        </Typography>
                                        <Typography variant="body-sm" tag="span">
                                            Before this customer record can be saved or selected, there are some required fields
                                            missing from the form below.
                                        </Typography>
                                    </Alert>
                                )}

                                <CustomerCardTabs
                                    activeTab={activeTab}
                                    activeSubTab={activeSubTab}
                                    hasCoBuyer={hasCoBuyer || Boolean(coBuyerDraft)}
                                    isBusinessBuyer={isBusinessBuyer}
                                    onTabChange={handleTabChange}
                                    onSubTabChange={handleSubTabChange}
                                    hidePrimaryTabs={Boolean(operation)}
                                    errorEntities={{
                                        [CUSTOMER_TABS.BUYER]: buyerHasContractErrors,
                                        [CUSTOMER_TABS.CO_BUYER]: coBuyerHasContractErrors,
                                        [CUSTOMER_TABS.INSURANCE]: insuranceHasContractErrors
                                    }}
                                />

                                {createPortal(
                                    <div data-testid="unified-customer-card-buyer-details">
                                        <CustomerForm
                                            ref={buyerFormRef}
                                            customerRole="buyer"
                                            initialValues={buyerDraft ?? buyer}
                                            capabilities={capabilities}
                                            onChange={handleBuyerFormChange}
                                            otherEmail={coBuyerLiveEmail}
                                            onAnalyticsEvent={onAnalyticsEvent}
                                            isBusinessConversionFlow={isBusinessConversionInProgress}
                                            isOperationDraft={Boolean(buyerDraft)}
                                            isAnyOperationInProgress={isAnyOperationInProgress}
                                            isDraftFromSearchResult={Boolean(buyerDraft) && isDraftFromSelection}
                                            personalInfoHeaderAction={personalInfoHeaderAction}
                                            onRequestOperation={requestOperation}
                                            hideCheckLoader={hideCheckLoader}
                                        />
                                    </div>,
                                    buyerHost
                                )}

                                {(hasCoBuyer || Boolean(coBuyerDraft)) &&
                                    createPortal(
                                        <div data-testid="unified-customer-card-cobuyer-details">
                                            <CustomerForm
                                                ref={coBuyerFormRef}
                                                customerRole="coBuyer"
                                                initialValues={coBuyerDraft ?? coBuyer}
                                                capabilities={capabilities}
                                                onChange={handleCoBuyerFormChange}
                                                otherEmail={buyerLiveEmail}
                                                buyerCustomerType={(buyerDraft ?? buyer)?.customerType}
                                                onAnalyticsEvent={onAnalyticsEvent}
                                                isOperationDraft={Boolean(coBuyerDraft)}
                                                isAnyOperationInProgress={isAnyOperationInProgress}
                                                isDraftFromSearchResult={Boolean(coBuyerDraft) && isDraftFromSelection}
                                                personalInfoHeaderAction={personalInfoHeaderAction}
                                                onRequestOperation={requestOperation}
                                                hideCheckLoader={hideCheckLoader}
                                            />
                                        </div>,
                                        coBuyerHost
                                    )}

                                {createPortal(
                                    <div data-testid="unified-customer-card-insurance">
                                        <InsuranceTab
                                            ref={insuranceFormRef}
                                            vehicleInsuranceInfo={vehicleInsuranceInfo}
                                            isVehicleInsuranceUpdated={isVehicleInsuranceUpdated}
                                            capabilities={capabilities}
                                            onChange={handleInsuranceFormChange}
                                            onContractingErrorsAlert={handleInsuranceContractErrors}
                                            isAnyOperationInProgress={isAnyOperationInProgress}
                                            dealId={dealId}
                                            hostApp={hostApp}
                                            onAnalyticsEvent={onAnalyticsEvent}
                                        />
                                    </div>,
                                    insuranceHost
                                )}

                                <div data-testid="unified-customer-card-content" ref={visibleMountRef} />
                            </>
                        )}
                    </CustomerCardErrorBoundary>
                </CardWrapper>
            </ThemeProvider>
        );
    }
);

UnifiedCustomerCard.displayName = 'UnifiedCustomerCard';

export default UnifiedCustomerCard;
